// The Types designer: the form you declare a section's editable slots on.
//
// Unlike types-values next door, nothing here is generated — /Types/Type is an
// ordinary schema and this is an ordinary schema-driven form over it. All the
// file really contains is the `config` blob saying which fields to show and in
// what order, plus the two selects whose options are not knowable from the
// schema alone: a param's type comes from the registry, and an `object` param's
// type comes from the other types the author has declared in this very form.
//
// Ported from the archived project's @repoflow/components/types-form, minus the
// object-selector block, whose param type was not ported. The image block came
// back with the param type of the same name.
import React from "react";
import _ from "lodash";
import { Component as FormSchema } from "@repoflow/form-schema";
import ContentTabs from "@repoflow/components/content-tabs";

// The name a param is addressed by in the markup, derived from its label so
// that the author writes one thing rather than two. Mirrors /Name/Required —
// lowercase, digits, single hyphens — because that is what the schema validates
// it against.
export const slugify = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

// `help` is a node per tab — `{ fields, subforms }` — rendered above that tab's
// form. The words are the caller's: what a field IS is the same everywhere,
// but where its value is read from is the app's business, and this package
// does not know whether it is a page shell or a section template.
export const Form = ({
  schema,
  paramTypes,
  onChange,
  values,
  widgets,
  readOnly = false,
  help = {},
}) => {
  const typeOptions = () => [
    { value: "", label: "Select a type" },
    ..._.map(values.types, ({ name }) => ({ value: name, label: name })),
  ];

  const paramOptions = () => [
    { value: "", label: "Select a type" },
    ..._.map(paramTypes, (type) => ({ value: type, label: type })),
  ];

  const typeConfig = {
    property: "type",
    widget: "select",
    options: paramOptions,
  };

  const config = {
    "/Input/Types": {
      fields: [{ property: "types", layout: "wide" }],
    },
    "/Types/Type": {
      listing: [
        { property: "name" },
        { property: "params", layout: "wide" },
      ],
      fields: [
        { property: "name" },
        { property: "description" },
        { property: "params", layout: "wide" },
        { property: "form", layout: "wide" },
      ],
    },
    "/Types/Param": {
      // Name, type, and what the type was given — the third is the `config`
      // branch, summarised by the paramDetails widget every branch names
      // (see the registry's params/types/index.js for why the schema names it
      // rather than this list).
      listing: ["name", "type", "config"],
      fields: [
        {
          property: "label",
          // Typing a label fills in the name. Two hops up because the field's
          // parent is the label field's own wrapper, not the param.
          onChange: ({ value, field: { parent: { parent: { setValues } } } }) => {
            setValues({ name: slugify(value) });
          },
        },
        "name",
        typeConfig,
        { property: "config", layout: "wide" },
        { property: "description", layout: "wide", textarea: true },
        "required",
        "hidden",
        "layout",
      ],
    },
    // Empty on purpose, and not for tidiness. The only property a paragraph
    // param declares is its default value, and the widget that authors one is a
    // Quill editor — a toolbar, a document and a lazily fetched chunk, inside
    // the modal a list row opens. A form re-render destroys a modal open over
    // it (P143), so a field that arrives a chunk-fetch late is the shape that
    // bites; and the value it would buy is the text a section shows before
    // anybody has filled it in, which is almost always none.
    //
    // The schema's own `{"ops":[]}` is what a param gets instead: form-schema
    // defaults every declared property whether or not a form shows it, which is
    // the same mechanism that fills a `code` element's three fields from a
    // dialog that displays none of them.
    "/Types/Param/Type/Paragraph": { fields: [] },
    // An image param's default: one field, wide, because the widget behind it is
    // a URL, a description and a preview across two columns. Unlike the
    // paragraph branch above this one is NOT empty — a default picture is a
    // reasonable thing to declare, and the widget costs nothing to mount: no
    // lazy chunk, so nothing arrives a frame late inside the modal a list row
    // opens it in.
    "/Types/Param/Type/Image": {
      fields: [{ property: "default", layout: "wide" }],
    },
    "/Types/Param/Type/Object": {
      fields: [
        { property: "type", widget: "select", options: typeOptions },
        "nullable",
      ],
    },
    "/Types/Param/Type/List": {
      fields: [typeConfig, { property: "config", layout: "wide" }],
    },
    "/Types/Param/Type/Options": {
      fields: [
        "list",
        {
          property: "default",
          widget: "select",
          // The default has to be one of the options just declared, so the
          // choices come from a sibling value rather than from the schema.
          options: ({ field }) => {
            const parent = field.parent ? field.parent.parent : null;
            const empty = [{ value: "", label: "Empty" }];

            if (!parent || !parent.values || !parent.values.list) {
              return empty;
            }

            return [
              ...empty,
              ...parent.values.list.map((value) => ({ value, label: value })),
            ];
          },
        },
        "allowEmpty",
      ],
    },
  };

  // Two tabs, because the two halves are edited at completely different rates.
  // "root" is the type the renderer actually resolves against and is what an
  // author touches every time; the rest exist only to be referenced by an
  // `object` or `list` param, and most sections never declare one. Showing them
  // in a single list buries the one that matters underneath the ones that
  // rarely do.
  const [root, ...rest] = values.types;

  const rootProps = {
    schema,
    widgets,
    readOnly,
    config: {
      ...config,
      // Only the params. "root" is always called root and never described, so
      // its name and description are noise on the one tab an author uses every
      // time.
      //
      // The label is NOT blanked here, though the screen this came from blanked
      // it. An empty <dt> renders an unlabelled field, which is what every
      // locator in the e2e suite addresses a form control by — and it is the
      // only handle a screen reader has on it either.
      "/Types/Type": {
        fields: [{ property: "params", layout: "wide" }],
      },
    },
    target: { $ref: "/Types/Type" },
    values: root,
    onChange: (val) => {
      const types = [...values.types];
      types[0] = val;
      onChange({ types });
    },
  };

  const restProps = {
    schema,
    widgets,
    readOnly,
    config,
    target: { $ref: "/Input/Types" },
    values: { types: rest },
    onChange: ({ types }) => {
      onChange({ types: [values.types[0], ...types] });
    },
  };

  return (
    <ContentTabs
      tabs={[
        {
          label: "Fields",
          content: (
            <>
              {help.fields || null}
              <FormSchema {...rootProps} />
            </>
          ),
        },
        {
          label: "Subforms",
          content: (
            <>
              {help.subforms || null}
              <FormSchema {...restProps} />
            </>
          ),
        },
      ]}
    />
  );
};
